import { Link } from "react-router-dom";
import { LogIn, LogOut, User as UserIcon, LayoutGrid, ChevronDown, FileText, LayoutDashboard, Menu } from "lucide-react";
import { useAuth, googleSignIn } from "@/lib/auth";
import { useConfig } from "@/lib/config";
import ThemeToggle from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Header({ minimal = false }) {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const showAdvisors = !!config?.show_advisors;
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-md bg-white/80 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group min-w-0">
          <img
            src="/logo.png"
            alt="SmartNote2Self"
            className="h-9 w-9 rounded-md object-cover transition-transform group-hover:-translate-y-0.5 shrink-0"
          />
          <div className="flex-col leading-tight hidden sm:flex">
            <span className="font-display text-[15px] font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              SmartNote2Self
            </span>
            <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Biz Resilience Scan
            </span>
          </div>
        </Link>

        {!minimal && !user && (
          <nav className="hidden lg:flex items-center gap-7 text-sm text-slate-600 dark:text-slate-300">
            <a href="/#what" data-testid="nav-what" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">What it does</a>
            <a href="/#how" data-testid="nav-how" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">How it works</a>
            <Link to="/pricing" data-testid="nav-pricing" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">Plans &amp; pricing</Link>
            <Link to="/learn-more" data-testid="nav-learn-more" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">Learn more</Link>
            {showAdvisors && (
              <Link to="/advisors" data-testid="nav-advisors" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">For advisors</Link>
            )}
            <Link to="/privacy" data-testid="nav-privacy" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">Privacy</Link>
          </nav>
        )}
        {!minimal && user && (
          <nav className="hidden lg:flex items-center gap-7 text-sm text-slate-600 dark:text-slate-300">
            <Link to="/pricing" data-testid="nav-pricing-signed" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">Plans &amp; pricing</Link>
            <Link to="/account" data-testid="nav-account-signed" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">Account &amp; history</Link>
            <Link to="/learn-more" data-testid="nav-learn-more-signed" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">Learn more</Link>
            {showAdvisors && (
              <Link to="/advisors" data-testid="nav-advisors-signed" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors">For advisors</Link>
            )}
          </nav>
        )}

        <div className="flex items-center gap-2">
          {!minimal && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="mobile-nav-trigger"
                  aria-label="Open menu"
                  className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {!user && (
                  <>
                    <DropdownMenuItem asChild>
                      <a href="/#what" data-testid="mobile-nav-what" className="cursor-pointer">What it does</a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href="/#how" data-testid="mobile-nav-how" className="cursor-pointer">How it works</a>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/pricing" data-testid="mobile-nav-pricing" className="cursor-pointer">Plans &amp; pricing</Link>
                </DropdownMenuItem>
                {user && (
                  <DropdownMenuItem asChild>
                    <Link to="/account" data-testid="mobile-nav-account" className="cursor-pointer">Account &amp; history</Link>
                  </DropdownMenuItem>
                )}
                {showAdvisors && (
                  <DropdownMenuItem asChild>
                    <Link to="/advisors" data-testid="mobile-nav-advisors" className="cursor-pointer">For advisors</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/learn-more" data-testid="mobile-nav-learn-more" className="cursor-pointer">Learn more</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/privacy" data-testid="mobile-nav-privacy" className="cursor-pointer">Privacy</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/mode" data-testid="mobile-nav-start" className="cursor-pointer font-semibold">Start Assessment</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <ThemeToggle />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="inline-flex items-center gap-2 h-9 px-2.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
                  ) : (
                    <UserIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  )}
                  <span className="text-[13px] max-w-[120px] truncate hidden sm:inline">
                    {user.name?.split(" ")[0] || user.email.split("@")[0]}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/account" data-testid="menu-account" className="cursor-pointer">
                    <LayoutGrid className="h-4 w-4 mr-2" /> Account & history
                  </Link>
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin-console" className="cursor-pointer">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Console
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/privacy" className="cursor-pointer">
                    <FileText className="h-4 w-4 mr-2" /> Privacy
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={googleSignIn}
              data-testid="signin-btn"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-black dark:bg-white text-white dark:text-black text-sm font-semibold hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors"
            >
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}

          <Link
            to="/mode"
            data-testid="header-start-btn"
            className="hidden md:inline-flex items-center gap-2 h-9 px-4 rounded-md bg-black dark:bg-teal-600 text-white text-sm font-semibold hover:bg-zinc-800 dark:hover:bg-teal-500 transition-colors"
          >
            Start Assessment
          </Link>
        </div>
      </div>
    </header>
  );
}
